import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// CSRF token storage
let csrfToken = null;

// Fetch CSRF token from backend
export async function fetchCsrfToken() {
  try {
    const response = await axios.get(`${API_BASE_URL}/csrf-token`, {
      withCredentials: true
    });
    csrfToken = response.data.csrfToken;
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    // Don't throw - allow app to continue without CSRF token
    // Backend will reject requests that need it
    return null;
  }
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Important for cookies
});

// Add request interceptor to include CSRF token
api.interceptors.request.use(
  (config) => {
    // Include CSRF token in header if available
    // Only for state-changing methods (POST, PUT, PATCH, DELETE)
    const methodsRequiringCsrf = ['post', 'put', 'patch', 'delete'];
    if (csrfToken && methodsRequiringCsrf.includes(config.method.toLowerCase())) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth endpoints that must never trigger a silent token refresh, to avoid
// infinite loops (the refresh call itself, login, and logout).
function isAuthFlowRequest(url = '') {
  return (
    url.includes('/auth/refresh') ||
    url.includes('/auth/google') ||
    url.includes('/auth/logout')
  );
}

// Track a single in-flight refresh so a burst of concurrent 401s results in
// exactly one call to /auth/refresh rather than a stampede.
let refreshPromise = null;

// Exchange the long-lived refresh_token cookie (7 days) for a fresh 1-hour
// access token. The /auth/refresh endpoint is CSRF-protected, so make sure a
// CSRF token is loaded first (the returning-user case can hit this before the
// app's startup fetchCsrfToken() has completed).
function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      if (!csrfToken) {
        await fetchCsrfToken();
      }
      // Goes through the request interceptor, which attaches X-CSRF-Token.
      await api.post('/auth/refresh');
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Handle CSRF token errors (403 with specific message)
    if (status === 403 && error.response?.data?.error?.includes('CSRF')) {
      // Try to refresh CSRF token once
      console.log('CSRF token invalid, refreshing...');
      await fetchCsrfToken();

      // Retry the original request with new token
      if (csrfToken && originalRequest && !originalRequest._csrfRetry) {
        originalRequest._csrfRetry = true;
        originalRequest.headers['X-CSRF-Token'] = csrfToken;
        return api.request(originalRequest);
      }
    }

    // Handle an expired/invalid access token. The 1-hour access token expires
    // long before the 7-day refresh token, so instead of dead-ending on
    // "Invalid or expired token" (which forced a full re-login / incognito),
    // silently mint a new access token and replay the original request once.
    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._authRetry &&
      !isAuthFlowRequest(originalRequest.url || '')
    ) {
      originalRequest._authRetry = true;
      try {
        await refreshAccessToken();
        return api.request(originalRequest);
      } catch (refreshError) {
        // Refresh token is missing or expired -> genuine logout. Fall through
        // and surface the original 401 so the app redirects to login.
        return Promise.reject(error);
      }
    }

    // Pass other errors through
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (googleToken) => api.post('/auth/google', { token: googleToken }),
  logout: () => api.post('/auth/logout'),
  getCurrentUser: () => api.get('/auth/me'),
  refreshToken: () => api.post('/auth/refresh')
};

// Intake API (public)
export const intakeAPI = {
  submit: (data) => api.post('/intake/submit', data),
  getStatus: (uuid) => api.get(`/intake/status/${uuid}`),
  checkPhone: (phoneNumber) => api.get('/intake/check-phone', { params: { phone_number: phoneNumber } })
};

// Submissions API (authenticated)
export const submissionsAPI = {
  getAll: (filters) => api.get('/submissions', { params: filters }),
  getById: (id) => api.get(`/submissions/${id}`),
  update: (id, data) => api.patch(`/submissions/${id}`, data),
  updateStatus: (id, status, reason) =>
    api.patch(`/submissions/${id}/status`, { status, reason }),
  submitToTwilio: (id) => api.post(`/submissions/${id}/submit`),
  // Destructive recovery: deletes a stale rejected Twilio verification whose
  // 7-day edit window has closed, then submits a fresh one. Admin only.
  recreateVerification: (id) => api.post(`/submissions/${id}/recreate-verification`),
  checkTwilioStatus: (id) => api.post(`/submissions/${id}/check-twilio-status`),
  checkProfileStatus: (id) => api.post(`/submissions/${id}/check-profile-status`),
  approve: (id) => api.post(`/submissions/${id}/approve`),
  reject: (id, reason) => api.post(`/submissions/${id}/reject`, { reason }),
  archive: (id, is_archived, reason) => api.post(`/submissions/${id}/archive`, { is_archived, reason }),
  addNote: (id, note_text) => api.post(`/submissions/${id}/notes`, { note_text }),
  getNotes: (id) => api.get(`/submissions/${id}/notes`),
  getAuditLog: (id, limit) => api.get(`/submissions/${id}/audit-log`, { params: { limit } }),
  delete: (id) => api.delete(`/submissions/${id}`),
  search: (query) => api.get('/submissions/search', { params: { q: query } }),
  getStats: (filters) => api.get('/submissions/stats', { params: filters })
};

// Documents API (authenticated)
export const documentsAPI = {
  upload: (submissionId, files) => {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('documents', file);
    });
    return api.post(`/documents/upload/${submissionId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  getBySubmissionId: (submissionId) => api.get(`/documents/submission/${submissionId}`),
  delete: (id) => api.delete(`/documents/${id}`)
};

// Users API (authenticated, admin only)
export const usersAPI = {
  getAll: () => api.get('/users'),
  getByEmail: (email) => api.get(`/users/${email}`),
  updateRole: (email, role) => api.patch(`/users/${email}/role`, { role }),
  activate: (email) => api.patch(`/users/${email}/activate`),
  deactivate: (email) => api.patch(`/users/${email}/deactivate`)
};

// Analytics API (authenticated, viewer+ role)
export const analyticsAPI = {
  getAnalytics: () => api.get('/analytics'),
  getDuplicates: () => api.get('/analytics/duplicates'),
  exportCSV: (filters) => {
    // Construct query string
    const params = new URLSearchParams(filters).toString();
    window.open(`/api/analytics/export${params ? '?' + params : ''}`, '_blank');
  }
};

// Bulk Operations API (authenticated, agent+ role)
export const bulkAPI = {
  updateStatus: (submissionIds, status, reason) =>
    api.post('/submissions/bulk/status', { submission_ids: submissionIds, status, reason }),
  submitToTwilio: (submissionIds) =>
    api.post('/submissions/bulk/submit-twilio', { submission_ids: submissionIds }),
  archive: (submissionIds, is_archived, reason) =>
    api.post('/submissions/bulk/archive', { submission_ids: submissionIds, is_archived, reason })
};

// CSV Import API (authenticated, agent+ role)
export const importAPI = {
  // Download CSV template
  downloadTemplate: () => {
    window.open('/api/submissions/import/template', '_blank');
  },

  // Validate CSV without importing (dry run)
  validate: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/submissions/import/validate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  // Import CSV file
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/submissions/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

export default api;
